import cx from "classnames";
import { Button } from "components/DesignSystem/Button/Button";
import { Hand } from "components/icons/Hand";
import { OptionUpload } from "components/icons/OptionUpload";
import Lottie from "lottie-react";
import { useCallback, useEffect, useState } from "react";
import { DropEvent, useDropzone } from "react-dropzone";
import DocumentUploadAndParse from "lottie/DocumentUploadAndParse.json";
import DocumentUploaded from "lottie/DocumentUploaded.json";
import { pluralize } from "utils/pluralize";
import { ParsingMessages } from "./ParsingMessages";

export type DropzoneStatus = "idle" | "loading" | "done";

type DropzoneProps = {
  status: DropzoneStatus;
  matchedCount: number;
  hasPendingItems: boolean;
  onFiles: (files: File[]) => void;

  variant?: "card" | "compact";
  onAreaClick?: () => void;
  onFilesDropped?: (files: File[]) => void;
};

const COMPACT_IDLE_COPY = "Drag & drop/upload files to pre-fill details";

const DRAG_DROP_EVENT_TYPE = "drop";

export const Dropzone = ({
  status,
  matchedCount,
  hasPendingItems,
  onFiles,
  variant = "card",
  onAreaClick,
  onFilesDropped,
}: DropzoneProps) => {
  const isCompact = variant === "compact";
  const isLoading = status === "loading";
  const [isPickingFiles, setIsPickingFiles] = useState(false);

  const handleDrop = useCallback(
    (acceptedFiles: File[], _rejections: unknown, event: DropEvent) => {
      setIsPickingFiles(false);
      if (acceptedFiles.length === 0) return;

      const isDragDrop = "type" in event && event.type === DRAG_DROP_EVENT_TYPE;
      if (isDragDrop) onFilesDropped?.(acceptedFiles);

      onFiles(acceptedFiles);
    },
    [onFiles, onFilesDropped]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    noKeyboard: false,
  });

  useEffect(() => {
    if (!isPickingFiles) return undefined;

    const stopPicking = (): void => setIsPickingFiles(false);

    const events = ["focus", "pointerdown", "pointermove", "keydown"] as const;

    events.forEach((event) => window.addEventListener(event, stopPicking));

    return () =>
      events.forEach((event) => window.removeEventListener(event, stopPicking));
  }, [isPickingFiles]);

  const handleClick = (): void => {
    setIsPickingFiles(true);
    onAreaClick?.();
  };

  const isActive = isDragActive || isPickingFiles;

  const iconClassName = cx(
    "t-flex t-items-center t-justify-center t-text-neutral-40 t-transition-colors group-hover:t-animate-icon-bob group-data-[active=true]:t-text-purple group-data-[active=true]:t-animate-icon-bob",
    {
      "t-mb-3": !isCompact,
      "group-hover:t-text-purple": isCompact,
    }
  );

  return (
    <div
      {...getRootProps({ onClick: handleClick })}
      data-active={isActive}
      className={cx(
        "t-group t-flex t-w-full t-cursor-pointer t-flex-col t-items-center t-justify-center t-gap-3 t-text-center t-outline-none t-caret-transparent t-select-none t-transition-colors",
        {
          "t-h-full t-min-h-[200px] t-rounded-2xl t-border-2 t-border-dashed t-border-neutral-10 t-bg-surface-lighter-grey t-p-6":
            !isCompact,
          "t-min-h-28 t-px-4 t-py-4": isCompact,
          "hover:t-bg-neutral-0": !isLoading && !isCompact,
          "!t-border-purple !t-bg-purple-0": isActive && !isCompact,
          "dropzone-parsing t-cursor-default !t-border-0 hover:t-bg-transparent":
            isLoading && !isActive,
        }
      )}
    >
      <input {...getInputProps()} />

      {isLoading && isCompact && (
        <div className="t-flex t-flex-col t-items-center t-gap-1">
          <Lottie
            className="t-h-10"
            animationData={DocumentUploadAndParse}
            loop
          />
          <ParsingMessages />
          <div className="t-text-caption-italic t-text-neutral-40">
            or drop to add more
          </div>
        </div>
      )}

      {isLoading && !isCompact && (
        <>
          <Lottie
            className="t-h-32"
            animationData={DocumentUploadAndParse}
            loop
          />
          <ParsingMessages />
          <Button type="button" size="small" customType="secondary">
            Upload more files
          </Button>
          <div className="t-text-body-sm t-font-light t-italic t-text-neutral-40">
            or drop to add more
          </div>
        </>
      )}

      {status === "done" && isCompact && (
        <div className="t-flex t-flex-col t-items-center t-gap-1">
          <Lottie className="t-h-10" animationData={DocumentUploaded} loop />

          <div className="t-text-subtext-sm t-text-text-100">
            Matched {pluralize(matchedCount, "document", "documents")}
          </div>

          <div className="t-text-caption t-text-neutral-40">
            {hasPendingItems
              ? "Drop more to tick the rest off your list."
              : "That's everything we needed — nice work."}
          </div>
        </div>
      )}

      {status === "done" && !isCompact && (
        <div className="t-flex t-flex-col t-items-center">
          <Lottie
            className="-t-mb-2 t-h-32"
            animationData={DocumentUploaded}
            loop
          />
          <div className="t-mb-1 t-text-subtext t-text-text-100">
            Matched {pluralize(matchedCount, "document", "documents")}
          </div>
          <div className="t-mb-5 t-text-body-sm t-font-light t-text-neutral-40">
            {hasPendingItems
              ? "Drop more to tick the rest off your list."
              : "That's everything we needed — nice work."}
          </div>
          <Button type="button" size="small" customType="secondary">
            Upload more files
          </Button>
          <div className="t-mt-1 t-text-body-sm t-font-light t-italic t-text-neutral-40">
            or drop to add more
          </div>
        </div>
      )}

      {status === "idle" && isCompact && (
        <div className="t-flex t-flex-col t-items-center t-gap-2">
          <span className={iconClassName}>
            <Hand className="t-h-6 t-w-6" />
          </span>

          <div className="t-text-subtext-sm t-text-text-30">
            {COMPACT_IDLE_COPY}{" "}
            <span className="t-text-caption">(optional)</span>
          </div>

          <Button type="button" size="small" customType="secondary">
            Select files
          </Button>
        </div>
      )}

      {status === "idle" && !isCompact && (
        <div className="t-flex t-flex-col t-items-center">
          <span className={iconClassName}>
            {hasPendingItems ? (
              <Hand className="t-h-7 t-w-7" />
            ) : (
              <OptionUpload className="t-h-7 t-w-7" />
            )}
          </span>
          <div className="t-mb-1 t-text-subtext-sm t-text-text-30">
            {hasPendingItems
              ? "Drag & drop files"
              : "Add anything extra (Optional)"}
          </div>
          <div className="t-mb-5 t-text-body-sm t-font-light t-italic t-text-neutral-40">
            {hasPendingItems
              ? "We will sort them into the right categories"
              : "Drop in any extra documents you'd like us to keep in record"}
          </div>
          <Button type="button" size="small" customType="secondary">
            Select files
          </Button>
        </div>
      )}
    </div>
  );
};
